/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import {
  applyMiddleware,
  combineReducers,
  compose,
  createStore,
  Middleware,
} from "redux"
import { thunk } from "redux-thunk"
import { grabXApi, partialMenuApi } from "queries"
import axios from "axios"
import { compact } from "lodash"
import axiosMiddleware from "redux-axios-middleware"
import { combineEpics, createEpicMiddleware } from "redux-observable"
import {
  httpRequest,
  foodMerchantHttpRequest,
  mexInsightsHttpRequest,
  delvPlatfromHttpRequest,
} from "utils/network"

import {
  actionCreator as orderManagerActionCreator,
  ActionCreatorTypeList as orderManagerActionCreatorTypeList,
  reducer as orderManagerReducer,
} from "order-manager/store/duck/order"
import {
  actionCreator as orderManagerDrawerActionCreator,
  reducer as orderManagerDrawerReducer,
} from "order-manager/store/duck/drawer"
import {
  actionCreator as orderManagerModalActionCreator,
  reducer as orderManagerModalReducer,
} from "order-manager/store/duck/modal"

import {
  actionCreator as orderManagerMerchantActionCreator,
  ActionCreatorTypeList as orderManagerMerchantActionCreatorTypeList,
  reducer as orderManagerMerchantReducer,
} from "order-manager/store/duck/merchantstore"

import { closeDrawersOrderManagerMiddleware } from "order-manager/store/middlewares"
import { generateAxiosHeader } from "utils/api_helper"
import { storageHelper } from "utils/storage"
import {
  actionCreator as merchantActionCreator,
  ActionCreatorTypeList as merchantActionCreatorTypeList,
  epics as merchantEpics,
  reducer as merchantReducer,
} from "./duck/merchant"
import {
  actionCreator as ordersActionCreator,
  ActionCreatorTypeList as ordersActionCreatorTypeList,
  epics as ordersEpics,
  reducer as ordersReducer,
} from "./duck/orders"
import {
  actionCreator as transactionsActionCreator,
  ActionCreatorTypeList as transactionsActionCreatorTypeList,
  epics as transactionsEpics,
  reducer as transactionsReducer,
} from "./duck/transactions"

import {
  actionCreator as menuActionCreator,
  reducer as menuReducer,
} from "./duck/menu"

import {
  actionCreator as globalActionCreator,
  reducer as globalReducer,
} from "./duck/global"

import {
  actionCreator as feedbackActionCreator,
  ActionCreatorTypeList as feedbackActionCreatorTypeList,
  reducer as feedbackReducer,
  epics as feedbackEpics,
} from "./duck/feedback"

import {
  actionCreator as feedbackDrawerActionCreator,
  ActionCreatorTypeList as feedbackDrawerActionCreatorTypeList,
  reducer as feedbackDrawerReducer,
} from "./duck/feedbackDrawer"

import {
  actionCreator as incidentsDrawerActionCreator,
  reducer as incidentsDrawerReducer,
} from "./duck/incidentsDrawer"

import { actionCreator as authActionCreator } from "./duck/auth"

import { reducer as bulkUploadReducer } from "./duck/bulkUpload/reducer"
import { epics as bulkUploadEpics } from "./duck/bulkUpload/epics"

import {
  showSuccessMiddleware,
  showErrorMiddleware,
  refreshMenuMiddleware,
  closeDrawersMiddleware,
  showConfirmMiddleware,
  showGlobalLoadingMiddleware,
  trackErrorMiddleware,
} from "./middlewares"

import { actionCreator as modifierGroupActionCreator } from "./duck/modifierGroup"

import {
  actionCreator as incidentActionCreator,
  ActionCreatorTypeList as incidentActionCreatorTypeList,
  reducer as incidentReducer,
} from "./duck/incidents"

import { EpicDependenciesType } from "./store.type"
import { requestInterceptor, responseInterceptor } from "./axios-interceptor"

const composeEnhancers =
  process.env.NODE_ENV === "production"
    ? compose
    : (window && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__) || compose

export const actionCreator = {
  global: globalActionCreator,
  merchant: merchantActionCreator,
  orders: ordersActionCreator,
  transactions: transactionsActionCreator,
  menu: menuActionCreator,
  modifierGroup: modifierGroupActionCreator,
  auth: authActionCreator,
  orderManager: orderManagerActionCreator,
  orderManagerDrawer: orderManagerDrawerActionCreator,
  orderManagerModal: orderManagerModalActionCreator,
  orderManagerMerchant: orderManagerMerchantActionCreator,
  feedback: feedbackActionCreator,
  feedbackDrawer: feedbackDrawerActionCreator,
  incidents: incidentActionCreator,
  incidentsDrawer: incidentsDrawerActionCreator,
}

type ActionCreatorTypeList =
  | merchantActionCreatorTypeList
  | ordersActionCreatorTypeList
  | transactionsActionCreatorTypeList
  | orderManagerActionCreatorTypeList
  | orderManagerMerchantActionCreatorTypeList
  | feedbackActionCreatorTypeList
  | feedbackDrawerActionCreatorTypeList
  | incidentActionCreatorTypeList

const epicMiddleware = createEpicMiddleware<
  ActionCreatorTypeList,
  ActionCreatorTypeList,
  EpicDependenciesType
>({
  dependencies: {
    httpRequest,
    foodMerchantHttpRequest,
    mexInsightsHttpRequest,
    delvPlatfromHttpRequest,
  },
})

const rootEpic = combineEpics(
  ...Object.values(merchantEpics),
  ...Object.values(ordersEpics),
  ...Object.values(transactionsEpics),
  ...Object.values(bulkUploadEpics),
  ...Object.values(feedbackEpics),
)

// TODO: move reducer to separate file
const rootReducer = combineReducers({
  global: globalReducer,
  merchant: merchantReducer,
  orders: ordersReducer,
  transactions: transactionsReducer,
  menu: menuReducer,
  orderManager: orderManagerReducer,
  orderManagerDrawer: orderManagerDrawerReducer,
  orderManagerModal: orderManagerModalReducer,
  orderManagerMerchant: orderManagerMerchantReducer,
  bulkUpload: bulkUploadReducer,
  feedback: feedbackReducer,
  feedbackDrawer: feedbackDrawerReducer,
  incidents: incidentReducer,
  incidentsDrawer: incidentsDrawerReducer,
  [grabXApi.reducerPath]: grabXApi.reducer,
  [partialMenuApi.reducerPath]: partialMenuApi.reducer,
})

type RootState = {
  global?: ReturnType<typeof globalReducer>
  merchant?: ReturnType<typeof merchantReducer>
  orders?: ReturnType<typeof ordersReducer>
  transactions?: ReturnType<typeof transactionsReducer>
  menu?: ReturnType<typeof menuReducer>
  orderManager?: ReturnType<typeof orderManagerReducer>
  orderManagerDrawer?: ReturnType<typeof orderManagerDrawerReducer>
  orderManagerModal?: ReturnType<typeof orderManagerModalReducer>
  orderManagerMerchant?: ReturnType<typeof orderManagerMerchantReducer>
  bulkUpload?: ReturnType<typeof bulkUploadReducer>
  feedback?: ReturnType<typeof feedbackReducer>
  incidents?: ReturnType<typeof incidentReducer>
  [grabXApi.reducerPath]?: ReturnType<typeof grabXApi.reducer>
  [partialMenuApi.reducerPath]?: ReturnType<typeof partialMenuApi.reducer>
}

const shouldSendCookie = storageHelper.isCookieAuthEnabled()

const axiosClient = axios.create({
  // timeout: 15000, // defaultTimeoutRequest
  responseType: "json",
  headers: generateAxiosHeader(),
  withCredentials: shouldSendCookie,
})

const isDev = process.env.NODE_ENV === "development"

requestInterceptor(axiosClient)
responseInterceptor(axiosClient, isDev)

export const defaultMiddlewares: Middleware[] = compact([
  showConfirmMiddleware, // needs to be placed before all
  isDev && require("redux-logger").default,
  isDev && require("./middlewares/mockResponse").mockResponseMiddleware,
  isDev &&
    require("./middlewares/mockFailedResponse").mockFailedResponseMiddleware,
  axiosMiddleware(axiosClient),
  showGlobalLoadingMiddleware,
  showSuccessMiddleware,
  showErrorMiddleware,
  trackErrorMiddleware,
])

const middlewares: Middleware[] = [
  // Thunk middleware must come first to handle async actions
  thunk as Middleware,
  // RTK Query middlewares
  grabXApi.middleware as Middleware,
  partialMenuApi.middleware as Middleware,
  // Others middlewares
  ...defaultMiddlewares,
  epicMiddleware,
  refreshMenuMiddleware,
  closeDrawersMiddleware,
  closeDrawersOrderManagerMiddleware,
]

export const setupStore = (initialStateOverride?: RootState) =>
  createStore(
    rootReducer,
    initialStateOverride,
    composeEnhancers(applyMiddleware(...middlewares)),
  )

export const store = setupStore()

epicMiddleware.run(rootEpic)
